import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { AiService, AiAssistant, AiMessage, AiRun, AiMessageContent } from '../../../../api-clients/composition';
import { useWebSocket } from '../../../ai/chat/api/AssistantWebSocket';

/**
 * Response from an AI insight prompt execution
 */
export interface AiInsightResponse {
  text: string;
  markdown?: string;
  entityCards?: Record<string, string>;
  intelligenceCard?: {
    headline?: string;
    byline?: string;
    fullText?: string;
  };
  threadId: string;
  messageId: string;
  runId?: string;
  assistantId?: string;
}

/**
 * Dashboard AI Context Value
 * 
 * Provides shared AI state and functionality for dashboard insight components.
 */
export interface DashboardAiContextValue {
  // Application/Agent identification
  applicationKey: string | null;
  assistantId: string | null;
  threadId: string | null;
  
  // State
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Last insight response (for reading without triggering new call)
  lastInsightResponse: AiInsightResponse | null;
  
  // Methods
  executeInsightPrompt: (
    prompt: string, 
    onStatusUpdate?: (status: string) => void,
    options?: {
      dashboardKey?: string;
      dashboardId?: string;
      insightCardId?: string;
    }
  ) => Promise<AiInsightResponse>;
  initializeAssistant: () => Promise<void>;
  clearContext: () => void;
}

const DashboardAiContext = createContext<DashboardAiContextValue | null>(null);

export interface DashboardAiProviderProps {
  children: React.ReactNode;
  applicationKey?: string;
  assistantKey?: string;
}

/**
 * Dashboard AI Context Provider
 * 
 * Manages AI assistant state for a dashboard, enabling:
 * - Shared assistant/thread across multiple insight cards
 * - Parallel prompt execution
 * - Thread persistence for conversation continuity
 */
export const DashboardAiProvider: React.FC<DashboardAiProviderProps> = ({
  children,
  applicationKey,
  assistantKey,
}) => {
  const [assistant, setAssistant] = useState<AiAssistant | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastInsightResponse, setLastInsightResponse] = useState<AiInsightResponse | null>(null);
  
  // Use refs to track initialization state
  const initializingRef = useRef(false);
  const assistantRef = useRef<AiAssistant | null>(null);
  const lastInsightResponseRef = useRef<AiInsightResponse | null>(null);
  
  // Track pending entity cards - messageId to resolve callback mapping
  const pendingEntityCardsRef = useRef<Map<string, (entityCards: Record<string, string>) => void>>(new Map());
  
  // Keep ref in sync with state
  useEffect(() => {
    lastInsightResponseRef.current = lastInsightResponse;
  }, [lastInsightResponse]);
  
  // Entity cards received from WebSocket
  const onEntityCards = useCallback((data: { messageId: string; entityCards: Record<string, string> }) => {
    console.log('🃏 [DashboardAiContext] Received entity cards via WebSocket');
    console.log(`   Message ID: ${data.messageId}`);
    console.log(`   Cards: ${Object.keys(data.entityCards || {}).join(', ')}`);
    console.log(`   Total cards: ${Object.keys(data.entityCards || {}).length}`);
    
    // Check for headline/byline specifically
    const headlineValue = data.entityCards['headline:headline'] || data.entityCards.headline;
    const bylineValue = data.entityCards['byline:byline'] || data.entityCards.byline;
    
    console.log(`   📰 headline:headline present: ${!!headlineValue}`);
    if (headlineValue) {
      console.log(`   📰 headline value: "${headlineValue}"`);
    } else {
      console.warn(`   ⚠️ headline:headline MISSING from entityCards!`);
    }
    
    console.log(`   📝 byline:byline present: ${!!bylineValue}`);
    if (bylineValue) {
      console.log(`   📝 byline value: "${bylineValue.substring(0, 100)}${bylineValue.length > 100 ? '...' : ''}"`);
    } else {
      console.warn(`   ⚠️ byline:byline MISSING from entityCards!`);
    }
    
    // Check if we have a pending promise waiting for these entity cards
    const resolver = pendingEntityCardsRef.current.get(data.messageId);
    if (resolver) {
      console.log('   ✅ Found pending resolver, calling it');
      resolver(data.entityCards);
      pendingEntityCardsRef.current.delete(data.messageId);
    } else {
      console.log('   ℹ️ No pending resolver for this messageId');
    }
    
    // If we have headline or byline, update or create lastInsightResponse
    if (headlineValue || bylineValue) {
      console.log('   📊 Found headline/byline in entity cards, updating lastInsightResponse');
      console.log(`   📊 Current lastInsightResponse messageId: ${lastInsightResponseRef.current?.messageId || 'none'}`);
      console.log(`   📊 WebSocket messageId: ${data.messageId}`);
      console.log(`   📊 MessageId match: ${lastInsightResponseRef.current?.messageId === data.messageId}`);
      
      setLastInsightResponse(prev => {
        // If we have an existing response with matching messageId, update it
        if (prev && prev.messageId === data.messageId) {
          console.log('   ✅ Updating existing lastInsightResponse with entity cards (messageId matches)');
          
          const intelligenceCard = {
            ...prev.intelligenceCard,
            headline: headlineValue || prev.intelligenceCard?.headline,
            byline: bylineValue || prev.intelligenceCard?.byline,
          };
          
          console.log(`   ✅ Updated intelligenceCard: headline=${!!intelligenceCard.headline}, byline=${!!intelligenceCard.byline}`);
          
          return {
            ...prev,
            entityCards: { ...prev.entityCards, ...data.entityCards },
            intelligenceCard,
          };
        }
        
        // If no existing response or different messageId, create/update with entity cards
        // CRITICAL: This is likely a race condition where WebSocket arrived before REST
        // We need to store the entity cards but NOT set the messageId yet, so REST can properly update later
        console.log('   📊 Creating/updating lastInsightResponse with entity cards (messageId mismatch)');
        console.log(`   📊 Previous messageId was: ${prev?.messageId || 'none'}`);
        console.log(`   📊 Previous text length: ${prev?.text?.length || 0}`);
        console.log(`   📊 Previous markdown length: ${prev?.markdown?.length || 0}`);
        console.log(`   📊 WebSocket messageId: ${data.messageId}`);
        
        // If we have previous text/markdown with a different messageId, keep that and just update entity cards
        // This ensures we don't lose the narrative when WebSocket sends updates for a different message
        if (prev?.text || prev?.markdown) {
          console.log('   ✅ Preserving existing text/markdown, only updating entity cards');
          return {
            ...prev,
            entityCards: { ...prev.entityCards, ...data.entityCards },
            intelligenceCard: {
              ...prev.intelligenceCard,
              headline: headlineValue || prev.intelligenceCard?.headline,
              byline: bylineValue || prev.intelligenceCard?.byline,
            },
            // Keep the previous messageId since we have content associated with it
          };
        }
        
        // No previous content - create new response with entity cards only
        // Store a special field to track this WebSocket data for later merging
        console.log('   ⚠️ No previous text/markdown - storing entity cards with pending messageId');
        return {
          text: '',
          markdown: '',
          entityCards: { ...(prev?.entityCards || {}), ...data.entityCards },
          intelligenceCard: {
            headline: headlineValue || prev?.intelligenceCard?.headline,
            byline: bylineValue || prev?.intelligenceCard?.byline,
          },
          threadId: prev?.threadId || '',
          // Store the WebSocket messageId - REST will merge with this if it matches
          messageId: data.messageId,
          // Mark that we're waiting for REST to provide the text
          _pendingTextFromRest: true,
        } as AiInsightResponse;
      });
    } else {
      // Just update entityCards if we have a matching response but no headline/byline
      console.warn('   ⚠️ No headline or byline found in entity cards!');
      setLastInsightResponse(prev => {
        if (prev && prev.messageId === data.messageId) {
          console.log('   📊 Updating lastInsightResponse entityCards (no headline/byline)');
          return {
            ...prev,
            entityCards: { ...prev.entityCards, ...data.entityCards },
          };
        }
        return prev;
      });
    }
  }, []);
  
  // Subscribe to WebSocket for entity cards
  useWebSocket({
    onRunStarted: undefined,
    onRunCompleted: undefined,
    onRunStatus: undefined,
    onEntityCards,
  });

  // Initialize the AI assistant
  const initializeAssistant = useCallback(async () => {
    if (!applicationKey && !assistantKey) {
      console.log('ℹ️ [DashboardAiContext] No applicationKey or assistantKey provided');
      return;
    }
    
    if (initializingRef.current) {
      console.log('⏳ [DashboardAiContext] Already initializing...');
      return;
    }
    
    initializingRef.current = true;
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`🚀 [DashboardAiContext] Initializing assistant for app: ${applicationKey}`);
      
      const aiAssistant = await AiService.aiControllerGetAssistant({
        applicationKey: applicationKey || undefined,
        assistantKey: assistantKey || undefined,
      });
      
      console.log(`✅ [DashboardAiContext] Assistant ready: ${aiAssistant.id}`);
      console.log(`   Thread: ${aiAssistant.threads?.[0]?.id || 'none'}`);
      
      setAssistant(aiAssistant);
      assistantRef.current = aiAssistant;
      setIsReady(true);
    } catch (err) {
      console.error('❌ [DashboardAiContext] Failed to initialize assistant:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize AI assistant');
    } finally {
      setIsLoading(false);
      initializingRef.current = false;
    }
  }, [applicationKey, assistantKey]);

  // Auto-initialize when applicationKey changes
  useEffect(() => {
    if (applicationKey || assistantKey) {
      initializeAssistant();
    }
    
    return () => {
      // Cleanup on unmount
      initializingRef.current = false;
    };
  }, [applicationKey, assistantKey, initializeAssistant]);

  // Execute an insight prompt and return the response
  const executeInsightPrompt = useCallback(async (
    prompt: string,
    onStatusUpdate?: (status: string) => void,
    options?: {
      dashboardKey?: string;
      dashboardId?: string;
      insightCardId?: string;
    }
  ): Promise<AiInsightResponse> => {
    console.log('🚀🚀🚀 [DashboardAiContext] executeInsightPrompt CALLED 🚀🚀🚀');
    console.log('   Prompt preview:', prompt?.substring(0, 100) + '...');
    console.log('   Options:', options);
    
    const currentAssistant = assistantRef.current || assistant;
    
    if (!currentAssistant?.id) {
      throw new Error('AI assistant not initialized');
    }
    
    const threadId = currentAssistant.threads?.[0]?.id;
    if (!threadId) {
      throw new Error('No thread available');
    }
    
    console.log(`📤 [DashboardAiContext] Executing insight prompt`);
    console.log(`   Prompt: "${prompt.substring(0, 100)}..."`);
    console.log(`   Dashboard identifiers:`, {
      dashboardKey: options?.dashboardKey || applicationKey || 'none',
      dashboardId: options?.dashboardId || 'none',
      insightCardId: options?.insightCardId || 'none',
    });
    
    onStatusUpdate?.('Starting...');
    
    // Create the message to send
    const content: AiMessageContent = {
      fileReferences: [],
      text: prompt,
      type: 'text',
    };
    
    // Build metadata with dashboard identifiers for caching
    const metadata: Record<string, any> = {
      type: 'insight',
      internal: false,
    };
    
    // Add dashboard identifiers if provided (for extension point caching)
    if (options?.dashboardKey || applicationKey) {
      metadata.dashboardKey = options?.dashboardKey || applicationKey;
    }
    if (options?.dashboardId) {
      metadata.dashboardId = options.dashboardId;
    }
    if (options?.insightCardId) {
      metadata.insightCardId = options.insightCardId;
    }
    
    const aiMessage: AiMessage = {
      assistantId: '',
      content: [content],
      createdAt: Date.now(),
      fileReferences: [],
      id: `insight_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metaData: JSON.stringify(metadata),
      parentAssistantId: currentAssistant.id,
      personId: '',
      role: 'user',
      runId: '',
      threadId: threadId,
    };
    
    // Send the message
    onStatusUpdate?.('Sending prompt...');
    const aiRun: AiRun = await AiService.aiControllerSendMessage(aiMessage);
    
    // CRITICAL: Log the full response from the server
    console.log('📊 [DashboardAiContext] Raw aiRun response from server:', {
      id: aiRun.id,
      status: aiRun.status,
      threadId: aiRun.threadId,
      hasMessagesPreview: !!aiRun.messagesPreview,
      messagesPreviewCount: aiRun.messagesPreview?.length || 0,
      messagesPreviewRoles: aiRun.messagesPreview?.map(m => m.role) || [],
      messagesPreviewIds: aiRun.messagesPreview?.map(m => m.id) || [],
    });
    
    // If we have messagesPreview, log detailed content info
    if (aiRun.messagesPreview && aiRun.messagesPreview.length > 0) {
      aiRun.messagesPreview.forEach((msg, idx) => {
        const contentInfo = msg.content 
          ? (typeof msg.content === 'string' 
            ? { type: 'string', preview: (msg.content as unknown as string).substring(0, 300) }
            : Array.isArray(msg.content)
              ? { type: 'array', length: msg.content.length, items: msg.content.map((c, i) => ({
                  idx: i,
                  itemType: typeof c,
                  hasText: !!(c as { text?: string })?.text,
                  textLength: (c as { text?: string })?.text?.length || 0,
                  textPreview: (c as { text?: string })?.text?.substring(0, 200) || '(no text)',
                })) }
              : { type: typeof msg.content }
          )
          : { type: 'undefined' };
        
        console.log(`   📊 [DashboardAiContext] messagesPreview[${idx}]:`, {
          id: msg.id,
          role: msg.role,
          contentInfo,
          hasMetaData: !!msg.metaData,
          metaDataPreview: msg.metaData?.substring(0, 200) || '(no metadata)',
        });
      });
    }
    
    // Poll for completion or use the preview messages
    let responseText = '';
    let entityCards: Record<string, string> = {};
    let messageId = '';
    let lastMessage: AiMessage | null = null;
    
    if (aiRun.messagesPreview && aiRun.messagesPreview.length > 0) {
      // Use preview messages (cached response)
      onStatusUpdate?.('Response received');
      
      // Find the last assistant message (not just the last message)
      const assistantMessages = aiRun.messagesPreview.filter(m => m.role === 'assistant');
      lastMessage = assistantMessages.length > 0 
        ? assistantMessages[assistantMessages.length - 1]
        : aiRun.messagesPreview[aiRun.messagesPreview.length - 1];
      
      // Extract text from content - handle multiple possible formats
      // Format 1: content is an array of AiMessageContent objects with .text property
      // Format 2: content is a string directly (legacy or simplified format)
      // Format 3: content items might be strings in the array
      if (lastMessage.content) {
        const rawContent = lastMessage.content as unknown;
        
        if (typeof rawContent === 'string') {
          // Content is a direct string
          responseText = rawContent;
          console.log('📊 [DashboardAiContext] Extracted text from string content:', responseText.length, 'chars');
        } else if (Array.isArray(rawContent) && rawContent.length > 0) {
          // Content is an array - iterate to find text
          for (const item of rawContent) {
            if (typeof item === 'string' && item.length > 0) {
              responseText = item;
              console.log('📊 [DashboardAiContext] Extracted text from string array item:', responseText.length, 'chars');
              break;
            } else if (item && typeof item === 'object') {
              const contentItem = item as { text?: string; type?: string };
              if (contentItem.text && contentItem.text.length > 0) {
                responseText = contentItem.text;
                console.log('📊 [DashboardAiContext] Extracted text from content object:', responseText.length, 'chars');
                break;
              }
            }
          }
        }
      }
      
      messageId = lastMessage.id || '';
      
      // CRITICAL: Log extraction result immediately
      console.log('🔍🔍🔍 [DashboardAiContext] TEXT EXTRACTION RESULT (messagesPreview path):');
      console.log('   responseText length:', responseText?.length || 0);
      console.log('   responseText preview:', responseText?.substring(0, 200) || '(EMPTY - EXTRACTION FAILED!)');
      if (!responseText) {
        console.error('🚨 [DashboardAiContext] TEXT EXTRACTION FAILED! lastMessage.content was:', JSON.stringify(lastMessage.content)?.substring(0, 500));
      }
      
      // Debug: Log message content structure with raw inspection
      const rawContentType = typeof lastMessage.content;
      const rawContentStr = rawContentType === 'string' 
        ? (lastMessage.content as unknown as string).substring(0, 200) 
        : null;
      
      const contentItems = Array.isArray(lastMessage.content) 
        ? lastMessage.content.map((item, idx) => ({
            idx,
            itemType: typeof item,
            type: (item as { type?: string })?.type || 'unknown',
            hasText: !!(item as { text?: string })?.text,
            textLength: (item as { text?: string })?.text?.length || 0,
          }))
        : null;
      
      console.log('📊 [DashboardAiContext] messagesPreview content debug:', {
        messageId,
        messageRole: lastMessage.role,
        totalMessages: aiRun.messagesPreview.length,
        assistantMessagesCount: assistantMessages.length,
        rawContentType,
        rawContentStr,
        contentIsArray: Array.isArray(lastMessage.content),
        contentLength: Array.isArray(lastMessage.content) ? lastMessage.content.length : 0,
        allContentItems: contentItems,
        firstContentItem: Array.isArray(lastMessage.content) && lastMessage.content[0] ? {
          itemType: typeof lastMessage.content[0],
          type: (lastMessage.content[0] as { type?: string })?.type,
          textLength: (lastMessage.content[0] as { text?: string })?.text?.length || 0,
          textPreview: (lastMessage.content[0] as { text?: string })?.text?.substring(0, 300) || '(no text in object)',
        } : null,
        responseTextLength: responseText.length,
        responseTextPreview: responseText.substring(0, 300) || '(EMPTY - extraction failed!)',
      });
      
      // CRITICAL: Warn if we couldn't extract text
      if (!responseText) {
        console.error('🚨 [DashboardAiContext] FAILED to extract text from messagesPreview!');
        console.error('   lastMessage.content:', JSON.stringify(lastMessage.content, null, 2)?.substring(0, 1000));
      }
      
      // Parse entity cards from metadata if available
      if (lastMessage.metaData) {
        try {
          const metadata = JSON.parse(lastMessage.metaData);
          if (metadata.entityCards) {
            entityCards = metadata.entityCards;
          }
        } catch {
          // Ignore parsing errors
        }
      }
    } else {
      // Wait for run to complete and load messages with status updates
      onStatusUpdate?.('Processing...');
      await waitForRunCompletion(aiRun.id, threadId, 30000, onStatusUpdate);
      
      onStatusUpdate?.('Loading response...');
      const messages = await AiService.aiControllerLoadMessages({ threadId });
      const assistantMessages = messages.filter(m => m.role === 'assistant');
      
      if (assistantMessages.length > 0) {
        lastMessage = assistantMessages[assistantMessages.length - 1];
        
        // Extract text from content - handle multiple possible formats
        // Format 1: content is an array of AiMessageContent objects with .text property
        // Format 2: content is a string directly (legacy or simplified format)
        // Format 3: content items might be strings in the array
        if (lastMessage.content) {
          const rawContent = lastMessage.content as unknown;
          
          if (typeof rawContent === 'string') {
            // Content is a direct string
            responseText = rawContent;
            console.log('📊 [DashboardAiContext] loadMessages: Extracted text from string content:', responseText.length, 'chars');
          } else if (Array.isArray(rawContent) && rawContent.length > 0) {
            // Content is an array - iterate to find text
            for (const item of rawContent) {
              if (typeof item === 'string' && item.length > 0) {
                responseText = item;
                console.log('📊 [DashboardAiContext] loadMessages: Extracted text from string array item:', responseText.length, 'chars');
                break;
              } else if (item && typeof item === 'object') {
                const contentItem = item as { text?: string; type?: string };
                if (contentItem.text && contentItem.text.length > 0) {
                  responseText = contentItem.text;
                  console.log('📊 [DashboardAiContext] loadMessages: Extracted text from content object:', responseText.length, 'chars');
                  break;
                }
              }
            }
          }
        }
        
        messageId = lastMessage.id || '';
        
        // CRITICAL: Log extraction result immediately
        console.log('🔍🔍🔍 [DashboardAiContext] TEXT EXTRACTION RESULT (loadMessages path):');
        console.log('   responseText length:', responseText?.length || 0);
        console.log('   responseText preview:', responseText?.substring(0, 200) || '(EMPTY - EXTRACTION FAILED!)');
        if (!responseText) {
          console.error('🚨 [DashboardAiContext] TEXT EXTRACTION FAILED! lastMessage.content was:', JSON.stringify(lastMessage.content)?.substring(0, 500));
        }
        
        // Debug: Log message content structure with raw inspection
        const rawContentType = typeof lastMessage.content;
        const rawContentStr = rawContentType === 'string' 
          ? (lastMessage.content as unknown as string).substring(0, 200) 
          : null;
        
        const loadMessagesContentItems = Array.isArray(lastMessage.content) 
          ? lastMessage.content.map((item, idx) => ({
              idx,
              itemType: typeof item,
              type: (item as { type?: string })?.type || 'unknown',
              hasText: !!(item as { text?: string })?.text,
              textLength: (item as { text?: string })?.text?.length || 0,
            }))
          : null;
        
        console.log('📊 [DashboardAiContext] loadMessages content debug:', {
          messageId,
          messageRole: lastMessage.role,
          totalMessages: messages.length,
          assistantMessagesCount: assistantMessages.length,
          rawContentType,
          rawContentStr,
          contentIsArray: Array.isArray(lastMessage.content),
          contentLength: Array.isArray(lastMessage.content) ? lastMessage.content.length : 0,
          allContentItems: loadMessagesContentItems,
          firstContentItem: Array.isArray(lastMessage.content) && lastMessage.content[0] ? {
            itemType: typeof lastMessage.content[0],
            type: (lastMessage.content[0] as { type?: string })?.type,
            textLength: (lastMessage.content[0] as { text?: string })?.text?.length || 0,
            textPreview: (lastMessage.content[0] as { text?: string })?.text?.substring(0, 300) || '(no text in object)',
          } : null,
          responseTextLength: responseText.length,
          responseTextPreview: responseText.substring(0, 300) || '(EMPTY - extraction failed!)',
        });
        
        // CRITICAL: Warn if we couldn't extract text
        if (!responseText) {
          console.error('🚨 [DashboardAiContext] FAILED to extract text from loadMessages!');
          console.error('   lastMessage.content:', JSON.stringify(lastMessage.content, null, 2)?.substring(0, 1000));
        }
        
        // Parse entity cards from metadata if available
        if (lastMessage.metaData) {
          try {
            const metadata = JSON.parse(lastMessage.metaData);
            if (metadata.entityCards) {
              entityCards = metadata.entityCards;
            }
          } catch {
            // Ignore parsing errors
          }
        }
      }
    }
    
    console.log(`✅ [DashboardAiContext] REST response received`);
    console.log(`   Response length: ${responseText.length} chars`);
    console.log(`   Response preview: ${responseText.substring(0, 500)}${responseText.length > 500 ? '...' : ''}`);
    console.log(`   Message ID: ${messageId}`);
    console.log(`   Last message exists: ${!!lastMessage}`);
    console.log(`   Last message has metaData: ${!!lastMessage?.metaData}`);
    console.log(`   EntityCards from REST: ${Object.keys(entityCards).length} cards`);
    if (lastMessage?.metaData) {
      console.log(`   metaData length: ${lastMessage.metaData.length} chars`);
      console.log(`   metaData preview: ${lastMessage.metaData.substring(0, 200)}${lastMessage.metaData.length > 200 ? '...' : ''}`);
    }
    
    // If we don't have entityCards from REST, wait for them from WebSocket (with timeout)
    if (Object.keys(entityCards).length === 0 && messageId) {
      console.log(`📡 [DashboardAiContext] No entity cards from REST, waiting for WebSocket...`);
      onStatusUpdate?.('Loading entity cards...');
      
      try {
        const wsEntityCards = await new Promise<Record<string, string>>((resolve, reject) => {
          // Set up a timeout
          const timeout = setTimeout(() => {
            pendingEntityCardsRef.current.delete(messageId);
            console.log(`⚠️ [DashboardAiContext] WebSocket entity cards timeout for ${messageId}`);
            resolve({}); // Resolve with empty object instead of rejecting
          }, 5000); // 5 second timeout
          
          // Store the resolver so the WebSocket handler can call it
          pendingEntityCardsRef.current.set(messageId, (cards) => {
            clearTimeout(timeout);
            resolve(cards);
          });
        });
        
        if (Object.keys(wsEntityCards).length > 0) {
          console.log(`✅ [DashboardAiContext] Received entity cards from WebSocket:`, Object.keys(wsEntityCards).join(', '));
          entityCards = wsEntityCards;
        }
      } catch (e) {
        console.log(`⚠️ [DashboardAiContext] WebSocket entity cards error:`, e);
      }
    }
    
    onStatusUpdate?.('Complete');
    console.log(`✅ [DashboardAiContext] Insight processing complete`);
    console.log(`   Final entityCards: ${Object.keys(entityCards).length} cards - ${Object.keys(entityCards).join(', ')}`);
    
    // Extract Intelligence Card fields - check entityCards first (token-based), then metadata (backend-extracted)
    let intelligenceCard: { headline?: string; byline?: string; fullText?: string } | undefined;
    
    // First, check entityCards for headline/byline (token-based approach with unified ${type:id} format)
    if (entityCards) {
      // Look for unified format keys: "headline:headline" and "byline:byline"
      const headlineValue = entityCards['headline:headline'] || entityCards.headline;
      const bylineValue = entityCards['byline:byline'] || entityCards.byline;
      
      if (headlineValue || bylineValue) {
        intelligenceCard = {};
        if (headlineValue) {
          intelligenceCard.headline = headlineValue;
          console.log(`📊 [DashboardAiContext] Found headline in entityCards: "${headlineValue}"`);
        }
        if (bylineValue) {
          intelligenceCard.byline = bylineValue;
          console.log(`📊 [DashboardAiContext] Found byline in entityCards: "${bylineValue.substring(0, 100)}${bylineValue.length > 100 ? '...' : ''}"`);
        }
      }
    }
    
    // Fallback: Try to get Intelligence Card fields from metadata (preferred - extracted by backend)
    if (lastMessage?.metaData) {
      try {
        const metadata = JSON.parse(lastMessage.metaData);
        console.log(`📊 [DashboardAiContext] Parsed metadata keys:`, Object.keys(metadata));
        console.log(`📊 [DashboardAiContext] Full metadata object:`, JSON.stringify(metadata, null, 2));
        
        // Also check metadata.entityCards for headline/byline (in case they weren't in the main entityCards)
        if (metadata.entityCards) {
          if (!intelligenceCard) intelligenceCard = {};
          // Look for unified format keys: "headline:headline" and "byline:byline"
          const metaHeadline = metadata.entityCards['headline:headline'] || metadata.entityCards.headline;
          const metaByline = metadata.entityCards['byline:byline'] || metadata.entityCards.byline;
          
          if (!intelligenceCard.headline && metaHeadline) {
            intelligenceCard.headline = metaHeadline;
            console.log(`📊 [DashboardAiContext] Found headline in metadata.entityCards: "${metaHeadline}"`);
          }
          if (!intelligenceCard.byline && metaByline) {
            intelligenceCard.byline = metaByline;
            console.log(`📊 [DashboardAiContext] Found byline in metadata.entityCards: "${metaByline.substring(0, 100)}${metaByline.length > 100 ? '...' : ''}"`);
          }
        }
        
        // Check metadata.intelligenceCard (backend-extracted from markdown)
        if (metadata.intelligenceCard) {
          if (!intelligenceCard) intelligenceCard = {};
          if (!intelligenceCard.headline && metadata.intelligenceCard.headline) {
            intelligenceCard.headline = metadata.intelligenceCard.headline;
            console.log(`📊 [DashboardAiContext] Found headline in metadata.intelligenceCard: "${metadata.intelligenceCard.headline}"`);
          }
          if (!intelligenceCard.byline && metadata.intelligenceCard.byline) {
            intelligenceCard.byline = metadata.intelligenceCard.byline;
            console.log(`📊 [DashboardAiContext] Found byline in metadata.intelligenceCard: "${metadata.intelligenceCard.byline.substring(0, 100)}${metadata.intelligenceCard.byline.length > 100 ? '...' : ''}"`);
          }
          if (metadata.intelligenceCard.fullText && !intelligenceCard.fullText) {
            intelligenceCard.fullText = metadata.intelligenceCard.fullText;
          }
        }
        
        if (intelligenceCard) {
          console.log(`📊 [DashboardAiContext] Final Intelligence Card fields: headline=${!!intelligenceCard.headline}, byline=${!!intelligenceCard.byline}`);
          if (intelligenceCard.headline) {
            console.log(`📊 [DashboardAiContext] Headline value: "${intelligenceCard.headline}"`);
          } else {
            console.log(`📊 [DashboardAiContext] ⚠️ Headline is missing`);
          }
          if (intelligenceCard.byline) {
            console.log(`📊 [DashboardAiContext] Byline value: "${intelligenceCard.byline.substring(0, 100)}${intelligenceCard.byline.length > 100 ? '...' : ''}"`);
          } else {
            console.log(`📊 [DashboardAiContext] ⚠️ Byline is missing`);
          }
        } else {
          console.log(`📊 [DashboardAiContext] ⚠️ No intelligenceCard found in metadata or entityCards`);
          console.log(`📊 [DashboardAiContext] Available metadata keys:`, Object.keys(metadata));
        }
      } catch (error) {
        console.error(`📊 [DashboardAiContext] Error parsing metadata:`, error);
        console.error(`📊 [DashboardAiContext] Raw metaData string:`, lastMessage.metaData);
      }
    } else {
      if (!intelligenceCard) {
        console.log(`📊 [DashboardAiContext] ⚠️ No metadata available on lastMessage`);
        console.log(`📊 [DashboardAiContext] lastMessage object:`, {
          id: lastMessage?.id,
          role: lastMessage?.role,
          hasContent: !!lastMessage?.content,
          contentLength: lastMessage?.content?.length,
          hasMetaData: !!lastMessage?.metaData,
        });
      }
    }
    
    // Try to extract structured data from the response (fallback for chat format)
    // Supports: { message: "...", entityCards: {...} } - Chat format
    let markdown = responseText;
    try {
      // Try to extract JSON from the response (might be wrapped in markdown code blocks)
      let jsonText = responseText.trim();
      jsonText = jsonText.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/\s*```$/, '');
      
      // Try to find JSON object in the text
      const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        
        // Handle chat format: { message: "...", entityCards: {...} }
        if (parsed.message) {
          markdown = parsed.message;
          if (parsed.entityCards) {
            entityCards = parsed.entityCards;
          }
        }
      }
    } catch {
      // Response is plain text/markdown, not JSON - use as-is
    }
    
    // Check if we already have entity cards from WebSocket (via onEntityCards handler)
    const existingResponse = lastInsightResponseRef.current;
    
    console.log('📊 [DashboardAiContext] Merging REST response with existing data:', {
      hasExistingResponse: !!existingResponse,
      existingMessageId: existingResponse?.messageId || 'none',
      restMessageId: messageId || 'none',
      existingTextLength: existingResponse?.text?.length || 0,
      restResponseTextLength: responseText.length,
      existingEntityCardsCount: Object.keys(existingResponse?.entityCards || {}).length,
      restEntityCardsCount: Object.keys(entityCards).length,
      existingPendingFromRest: !!(existingResponse as any)?._pendingTextFromRest,
    });
    
    // If REST response is empty (cached response), use WebSocket data if available
    // Check if WebSocket has data - prefer exact messageId match, but also use if REST is empty and WebSocket has data
    const hasExactMatch = existingResponse?.messageId === messageId && messageId;
    const hasWebSocketDataForThread = !messageId && // REST didn't provide messageId (cached response)
      existingResponse && 
      existingResponse.entityCards && 
      Object.keys(existingResponse.entityCards).length > 0;
    
    // Also merge if WebSocket arrived first and is waiting for REST text
    const webSocketWaitingForRest = existingResponse && 
      (existingResponse as any)._pendingTextFromRest && 
      responseText.length > 0;
    
    const shouldUseWebSocketData = hasExactMatch || hasWebSocketDataForThread || webSocketWaitingForRest;
    
    console.log('📊 [DashboardAiContext] Merge decision:', {
      hasExactMatch,
      hasWebSocketDataForThread,
      webSocketWaitingForRest,
      shouldUseWebSocketData,
    });
    
    const existingEntityCards = shouldUseWebSocketData && existingResponse?.entityCards 
      ? existingResponse.entityCards 
      : {};
    
    // Merge entity cards: prefer REST metadata, but fall back to WebSocket if REST is empty
    const mergedEntityCards = Object.keys(entityCards).length > 0 
      ? { ...existingEntityCards, ...entityCards } // REST has priority
      : existingEntityCards; // Use WebSocket cards if REST is empty
    
    // Also merge intelligenceCard if it exists from WebSocket
    const mergedIntelligenceCard = shouldUseWebSocketData && existingResponse?.intelligenceCard
      ? { ...existingResponse.intelligenceCard, ...intelligenceCard }
      : intelligenceCard;
    
    // Use WebSocket messageId if REST didn't provide one (cached response)
    const finalMessageId = messageId || existingResponse?.messageId || '';
    const finalText = responseText || existingResponse?.text || '';
    const finalMarkdown = markdown || existingResponse?.markdown || finalText;
    
    // Critical warning if we end up with empty text but have entity cards
    if (!finalText && Object.keys(mergedEntityCards).length > 0) {
      console.error('🚨 [DashboardAiContext] CRITICAL: Final response has NO text but HAS entity cards!');
      console.error('   This means the chat will show placeholder text instead of the full narrative.');
      console.error('   responseText length:', responseText.length);
      console.error('   existingResponse?.text length:', existingResponse?.text?.length || 0);
      console.error('   markdown length:', markdown.length);
      console.error('   mergedEntityCards count:', Object.keys(mergedEntityCards).length);
    }
    
    const response: AiInsightResponse = {
      text: finalText,
      markdown: finalMarkdown,
      entityCards: mergedEntityCards,
      intelligenceCard: mergedIntelligenceCard,
      threadId,
      messageId: finalMessageId,
      runId: aiRun.id,
      assistantId: currentAssistant.id,
    };
    
    // Store the response so other components can read it without triggering new calls
    console.log('🔴🔴🔴 [DashboardAiContext] STORING lastInsightResponse - THIS IS THE CRITICAL MOMENT 🔴🔴🔴');
    console.log('📊 [DashboardAiContext] Final response object:', {
      hasHeadline: !!response.intelligenceCard?.headline,
      hasByline: !!response.intelligenceCard?.byline,
      entityCardsKeys: response.entityCards ? Object.keys(response.entityCards) : [],
      entityCardsCount: Object.keys(response.entityCards || {}).length,
      mergedFromWebSocket: Object.keys(existingEntityCards).length > 0 && Object.keys(entityCards).length === 0,
      textLength: response.text?.length || 0,
      markdownLength: response.markdown?.length || 0,
      textPreview: response.text?.substring(0, 300) || '(EMPTY - THIS IS THE PROBLEM!)',
      markdownPreview: response.markdown?.substring(0, 300) || '(EMPTY)',
      messageId: response.messageId,
      threadId: response.threadId,
    });
    
    // CRITICAL: Trace back where text came from
    console.log('📊 [DashboardAiContext] Text source tracing:');
    console.log('   responseText variable:', responseText?.length || 0, 'chars');
    console.log('   responseText preview:', responseText?.substring(0, 200) || '(empty)');
    console.log('   markdown variable:', markdown?.length || 0, 'chars');
    console.log('   finalText (used in response):', finalText?.length || 0, 'chars');
    console.log('   finalMarkdown (used in response):', finalMarkdown?.length || 0, 'chars');
    
    if (!response.text && !response.markdown) {
      console.error('🚨🚨🚨 [DashboardAiContext] CRITICAL: Response has NO TEXT! The "Show Me Why" will show fallback content!');
    }
    
    setLastInsightResponse(response);
    
    return response;
  }, [assistant]);

  // Helper to wait for run completion with status updates
  const waitForRunCompletion = async (
    runId: string,
    threadId: string,
    maxWaitMs: number = 30000,
    onStatusUpdate?: (status: string) => void
  ): Promise<void> => {
    const startTime = Date.now();
    const pollInterval = 1000;
    let lastStatus = '';
    
    while (Date.now() - startTime < maxWaitMs) {
      const run = await AiService.aiControllerLoadRun({ runId, threadId });
      
      // Map run status to user-friendly messages
      const statusMessages: Record<string, string> = {
        'queued': 'Queued...',
        'in_progress': 'Analyzing...',
        'requires_action': 'Processing tool calls...',
        'cancelling': 'Cancelling...',
        'cancelled': 'Cancelled',
        'failed': 'Failed',
        'completed': 'Completed',
      };
      
      const statusMessage = statusMessages[run.status] || `Processing (${run.status})...`;
      
      // Only update if status changed
      if (statusMessage !== lastStatus) {
        lastStatus = statusMessage;
        onStatusUpdate?.(statusMessage);
      }
      
      if (run.status === 'completed' || run.status === 'failed' || run.status === 'cancelled') {
        return;
      }
      
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    throw new Error('Timeout waiting for AI response');
  };

  // Clear the context
  const clearContext = useCallback(() => {
    setAssistant(null);
    assistantRef.current = null;
    setIsReady(false);
    setError(null);
  }, []);

  const contextValue: DashboardAiContextValue = {
    applicationKey: applicationKey || null,
    assistantId: assistant?.id || null,
    threadId: assistant?.threads?.[0]?.id || null,
    isReady,
    isLoading,
    error,
    lastInsightResponse,
    executeInsightPrompt,
    initializeAssistant,
    clearContext,
  };

  return (
    <DashboardAiContext.Provider value={contextValue}>
      {children}
    </DashboardAiContext.Provider>
  );
};

/**
 * Hook to access the Dashboard AI context
 */
export const useDashboardAiContext = (): DashboardAiContextValue | null => {
  return useContext(DashboardAiContext);
};

/**
 * Hook to access the Dashboard AI context with required check
 * Throws if used outside of DashboardAiProvider
 */
export const useDashboardAiContextRequired = (): DashboardAiContextValue => {
  const context = useContext(DashboardAiContext);
  
  if (!context) {
    throw new Error('useDashboardAiContextRequired must be used within a DashboardAiProvider');
  }
  
  return context;
};

export default DashboardAiContext;
